// Phones and tablets: the menu page (the tab bar's More): everything the sidebar holds, as a page,
// rather than a drawer over the screen.
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router';
import { LogOutIcon, MessageSquareIcon, PlusIcon } from 'lucide-react';
import { useFeatures, useMarket } from '@/lib/client/use-market';
import { api } from '@/lib/api';
import { displayName, useMe } from '@/lib/client/use-me';
import { Link } from '@/lib/nav';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AppHeader, Page, SidebarBody, kiteDue } from './app-header';
import { KiteLogin } from './market-ui';
import { carry, dateSeed, signOut } from './common';
import { UserAvatar } from './user-avatar';

/** "Good morning" by the hour in India (the market's clock), whatever the phone's time zone. */
function greeting() {
  const h = new Date(Date.now() + (330 + new Date().getTimezoneOffset()) * 60000).getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

export function MenuApp() {
  const [seed] = useState(dateSeed);
  const { status, mode, live, user, role } = useMarket({ index: 'NIFTY500', seed });
  const query = carry({ index: 'NIFTY500', live, seed });
  const me = useMe();
  const [confirmOut, setConfirmOut] = useState(false);
  const first = displayName(me).split(' ')[0];
  // The desk's conversations live here on phones (the desk page keeps its sidebar on wider screens).
  const feat = useFeatures();
  const deskPage = feat.on('desk_page') && !!me?.access;
  const [threads, setThreads] = useState<{ id: string; title: string; updatedAt: string }[] | null>(null);
  useEffect(() => {
    if (!deskPage) return;
    void api('/v1/threads').then((r) => (r.ok ? r.json() : [])).then(setThreads).catch(() => setThreads([]));
  }, [deskPage]);
  const [wide, setWide] = useState(() => typeof matchMedia !== 'undefined' && matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    document.title = 'Menu · MoneyHQ';
    const mq = matchMedia('(min-width: 1024px)');
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  // Laptops have the sidebar: no menu page there.
  if (wide) return <Navigate to="/" replace />;
  return (
    <>
      <AppHeader section="menu" mode={mode} status={status} user={user} role={role} query={query} />
      <Page>
        {/* The member first: who is signed in, and signing out. */}
        <div className="flex items-center gap-3 rounded-2xl border bg-card p-4">
          <Link href={`/settings${query}`} className="flex min-w-0 flex-1 items-center gap-3" title={me?.user}>
            <UserAvatar me={me} size="md" className="size-12 text-lg" />
            <span className="min-w-0">
              <span className="block truncate font-heading text-[19px] font-semibold text-ink">{greeting()}{first ? `, ${first}` : ''}</span>
              <span className="block truncate text-[13px] text-muted-foreground">{role === 'admin' ? 'Admin' : status?.locked ? 'Awaiting approval' : 'Member'}{me?.user ? ` · ${me.user}` : ''}</span>
            </span>
          </Link>
          <button type="button" onClick={() => setConfirmOut(true)} aria-label="Sign out" title="Sign out"
            className="grid size-10 flex-none place-items-center rounded-full border text-muted-foreground hover:bg-bear-soft hover:text-bear">
            <LogOutIcon className="size-[18px]" />
          </button>
        </div>
        {kiteDue(role, status) && (
          <div className="mt-4 flex items-center gap-3 rounded-2xl border border-orange-500/40 bg-card p-4">
            <span className="min-w-0 flex-1">
              <span className="block text-[15px] font-semibold text-ink">Kite is logged out</span>
              <span className="block text-[13px] text-muted-foreground">Live prices stop until you log in again (Kite sessions end each morning).</span>
            </span>
            <KiteLogin status={status} />
          </div>
        )}
        {deskPage && (
          <div className="mt-4 overflow-hidden rounded-2xl border bg-card">
            <div className="flex items-center justify-between px-5 pt-4 pb-1.5">
              <span className="text-[11px] font-semibold tracking-[0.08em] text-muted-foreground/80 uppercase">Desk conversations</span>
              <Link href="/desk?new=1" className="inline-flex h-8 items-center gap-1 rounded-full border px-3 text-[12.5px] font-medium text-brand-strong"><PlusIcon className="size-3.5" />New chat</Link>
            </div>
            <div className="px-2 pb-2">
              {threads === null && <p className="px-3 py-2 text-[13px] text-muted-foreground">Loading…</p>}
              {threads?.length === 0 && <p className="px-3 py-2 text-[13px] text-muted-foreground">No conversations yet.</p>}
              {threads?.slice(0, 8).map((t) => (
                <Link key={t.id} href={`/desk?thread=${encodeURIComponent(t.id)}`} className="flex h-10 items-center gap-3 rounded-lg px-3 text-[14px] text-ink hover:bg-tint">
                  <MessageSquareIcon className="size-[18px] flex-none text-muted-foreground" /><span className="truncate">{t.title}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
        <div className="mt-4 overflow-hidden rounded-2xl border bg-card [&>nav]:px-2 [&>nav]:pb-2">
          <SidebarBody page section="menu" query={query} role={role} locked={!!status?.locked} status={status}
            collapsed={false} onCollapse={() => {}} drawer={false} onClose={() => {}} />
        </div>
        <Dialog open={confirmOut} onOpenChange={setConfirmOut}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Sign out?</DialogTitle>
              <DialogDescription>You&apos;ll need to sign in with Google again on this device.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOut(false)}>Cancel</Button>
              <Button variant="destructive" onClick={() => void signOut()}><LogOutIcon />Sign out</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Page>
    </>
  );
}
